/**
 * ONE TAP — UI root.
 *
 * Owns every pixel of DOM that sits over the canvas: menu, lobby, HUD,
 * scoreboard, settings. Nothing in here touches Three.js and nothing in the
 * renderer touches these nodes; the two layers meet only at `WorldState`,
 * `LobbyInfo` and `GameEvent`.
 *
 * Everything is `pointer-events: none` by default so the game can never be
 * blocked by an invisible overlay; only the interactive screens opt back in.
 */

/// <reference types="vite/client" />

import type { GameEvent, LobbyInfo, WorldState } from '@shared/types';
import { RoundPhase } from '@shared/types';
import { Hud } from './hud';
import { LobbyScreen } from './lobby';
import { MenuScreen, extractCodeBody } from './menu';
import { Scoreboard } from './scoreboard';
import { SettingsPanel, applySettingsToDocument, getSettings } from './settings';
import './style.css';

export interface UIHooks {
  onCreateRoom(name: string): void;
  onJoinRoom(code: string, name: string): void;
  onReady(r: boolean): void;
  onSwitchTeam(t: number): void;
  onStartMatch(): void;
  onSetBots(f: boolean): void;
  onLeave(): void;
  onSensitivity(v: number): void;
  onVolume(v: number): void;
}

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'error' | 'closed';
type Screen = 'menu' | 'lobby' | 'game';

/**
 * Custom events dispatched on `window`, for the parts of the UI that need
 * something from the host app but have no slot in the fixed `UIHooks`:
 *
 *   `onetap:requestlock` — the player closed the pause/settings overlay and
 *                          wants to be back in the game; re-request pointer lock.
 *   `onetap:uisound`     — `{ detail: { kind } }` for the audio engine's `ui()`.
 */
export const EVT_REQUEST_LOCK = 'onetap:requestlock';
export const EVT_UI_SOUND = 'onetap:uisound';

export type UiSoundKind = 'hover' | 'click' | 'back' | 'error' | 'ready' | 'countdown';

export class UI {
  private readonly layer: HTMLElement;

  private readonly menu: MenuScreen;
  private readonly lobby: LobbyScreen;
  private readonly hud: Hud;
  private readonly scoreboard: Scoreboard;
  private readonly settings: SettingsPanel;

  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onKeyUp: (e: KeyboardEvent) => void;
  private readonly onBlur: () => void;

  private screen: Screen = 'menu';
  private connection: ConnectionState = 'idle';
  private matchOver = false;

  constructor(root: HTMLElement, hooks: UIHooks) {
    applySettingsToDocument();

    const layer = document.createElement('div');
    layer.className = 'ot-ui';
    layer.dataset.screen = 'menu';
    this.layer = layer;

    const emitSound = (kind: UiSoundKind): void => {
      window.dispatchEvent(new CustomEvent(EVT_UI_SOUND, { detail: { kind } }));
    };

    this.settings = new SettingsPanel({
      onSensitivity: (v) => hooks.onSensitivity(v),
      onVolume: (v) => hooks.onVolume(v),
      onResume: () => {
        if (this.screen === 'game') window.dispatchEvent(new CustomEvent(EVT_REQUEST_LOCK));
      },
      onUiSound: emitSound,
    });

    this.menu = new MenuScreen({
      onCreateRoom: (name) => {
        this.menu.setBusy(true, 'CREATING…');
        hooks.onCreateRoom(name);
      },
      onJoinRoom: (code, name) => {
        this.menu.setBusy(true, 'JOINING…');
        hooks.onJoinRoom(code, name);
      },
      onOpenSettings: () => this.settings.open(false),
      onUiSound: emitSound,
    });

    this.lobby = new LobbyScreen({
      onReady: (r) => hooks.onReady(r),
      onSwitchTeam: (t) => hooks.onSwitchTeam(t),
      onStartMatch: () => hooks.onStartMatch(),
      onSetBots: (f) => hooks.onSetBots(f),
      onLeave: () => hooks.onLeave(),
      onOpenSettings: () => this.settings.open(false),
      onUiSound: emitSound,
    });

    this.hud = new Hud();
    this.scoreboard = new Scoreboard();

    // NOTE: `appendChild`, not `append`. The project tsconfig loads
    // @cloudflare/workers-types globally, whose HTMLRewriter `Element` shadows the
    // DOM `append`/`prepend` signatures. Every DOM insertion in src/client/ui uses
    // appendChild/insertBefore for that reason.
    for (const el of [
      this.hud.element,
      this.scoreboard.element,
      this.menu.element,
      this.lobby.element,
      this.settings.element,
    ]) {
      layer.appendChild(el);
    }
    root.appendChild(layer);

    // Deep link: `#ONETAP-ABCD` prefills the join code so a shared link is one click.
    const hash = typeof location !== 'undefined' ? location.hash.replace(/^#/, '') : '';
    if (hash) {
      const body = extractCodeBody(hash);
      if (body.length > 0) this.menu.prefillCode(body);
    }

    // --- global keys --------------------------------------------------------
    this.onKeyDown = (e: KeyboardEvent): void => {
      if (e.code === 'Tab' && this.screen === 'game') {
        e.preventDefault();
        if (!e.repeat) this.scoreboard.setVisible(true);
        return;
      }
      if (e.code === 'Escape' && this.screen === 'game' && !this.settings.isOpen()) {
        // The browser has already dropped pointer lock by the time this fires,
        // so opening the pause panel here is exactly what the player expects.
        e.preventDefault();
        this.settings.open(true);
      }
    };
    this.onKeyUp = (e: KeyboardEvent): void => {
      if (e.code === 'Tab') this.scoreboard.setVisible(false);
    };
    this.onBlur = (): void => this.scoreboard.setVisible(false);

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);

    this.showMenu();
  }

  // -------------------------------------------------------------------------
  // Screens
  // -------------------------------------------------------------------------

  showMenu(): void {
    this.screen = 'menu';
    this.layer.dataset.screen = 'menu';
    this.matchOver = false;
    this.scoreboard.setVisible(false);
    this.scoreboard.setPinned(false);
    this.lobby.hide();
    this.hud.hide();
    this.menu.show();
    this.menu.setConnection(this.connection === 'connected' ? 'idle' : this.connection);
  }

  showLobby(l: LobbyInfo, localId: string): void {
    this.screen = 'lobby';
    this.layer.dataset.screen = 'lobby';
    this.matchOver = false;
    this.scoreboard.setVisible(false);
    this.scoreboard.setPinned(false);
    this.menu.setBusy(false);
    this.menu.hide();
    this.hud.hide();
    this.lobby.show();
    this.lobby.update(l, localId);
  }

  showGame(): void {
    this.screen = 'game';
    this.layer.dataset.screen = 'game';
    this.matchOver = false;
    this.menu.setBusy(false);
    this.menu.hide();
    this.lobby.hide();
    this.scoreboard.setPinned(false);
    this.hud.show();
  }

  // -------------------------------------------------------------------------
  // Per-frame
  // -------------------------------------------------------------------------

  updateHud(w: WorldState, localId: string, dtSec: number): void {
    if (this.screen !== 'game') return;
    this.hud.update(w, localId, dtSec);

    // At match end the scoreboard latches open — nobody should hold TAB to read
    // the result screen.
    const over = w.match.phase === RoundPhase.MatchEnd;
    if (over !== this.matchOver) {
      this.matchOver = over;
      this.scoreboard.setPinned(over);
    }
    this.scoreboard.update(w, localId);
  }

  // -------------------------------------------------------------------------
  // Events + status
  // -------------------------------------------------------------------------

  handle(e: GameEvent, w: WorldState, localId: string): void {
    if (this.screen === 'game') this.hud.handle(e, w, localId);
  }

  setConnection(s: ConnectionState, msg?: string): void {
    this.connection = s;
    this.menu.setConnection(s, msg);
    this.lobby.setConnection(s, msg);
    this.hud.setConnection(s, msg);
    this.layer.dataset.connection = s;
    if (s === 'error' || s === 'closed') this.menu.setBusy(false);
  }

  setStats(fps: number, pingMs: number): void {
    this.hud.setStats(fps, pingMs);
  }

  /** Current persisted settings — handy for the host app on boot. */
  currentSettings(): ReturnType<typeof getSettings> {
    return getSettings();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.menu.dispose();
    this.lobby.dispose();
    this.hud.dispose();
    this.scoreboard.dispose();
    this.settings.dispose();
    this.layer.remove();
  }
}
